import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { clinicsApi, rolesApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiPlus,
  HiX,
  HiPencil,
  HiTrash,
  HiArrowLeft,
  HiOutlineUserCircle,
  HiOutlineStar,
} from 'react-icons/hi';
import ConfirmDialog from '../../components/ConfirmDialog';

const ClinicMembers = () => {
  const { id } = useParams();
  const clinicId = parseInt(id, 10);

  const [clinic, setClinic] = useState(null);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ user_id: '', role_id: '', is_default: false });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, message: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [clinicRes, membersRes, usersRes, rolesRes] = await Promise.all([
        clinicsApi.getById(clinicId),
        clinicsApi.getMembers(clinicId),
        clinicsApi.getAllUsers(),
        rolesApi.getAll(),
      ]);
      setClinic(clinicRes.data.data);
      setMembers(membersRes.data.data || []);
      setUsers(usersRes.data.data || []);
      setRoles(rolesRes.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load clinic members');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    if (Number.isFinite(clinicId)) fetchData();
  }, [clinicId, fetchData]);

  const activeMemberIds = new Set(members.filter(m => m.is_active === 1 || m.is_active === true).map(m => m.user_id));
  const assignableUsers = users.filter(u => !activeMemberIds.has(u.id));

  const handleAdd = () => {
    setEditing(null);
    const firstUserId = assignableUsers[0]?.id || '';
    const firstRoleId = roles[0]?.id || '';
    setFormData({ user_id: firstUserId, role_id: firstRoleId, is_default: false });
    setShowForm(true);
  };

  const handleEdit = (member) => {
    setEditing(member);
    setFormData({
      user_id: member.user_id,
      role_id: member.role_id,
      is_default: member.is_default === 1 || member.is_default === true,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id || !formData.role_id) {
      toast.error('Please select a user and a role.');
      return;
    }
    try {
      if (editing) {
        await clinicsApi.updateMember(clinicId, editing.user_id, {
          role_id: parseInt(formData.role_id, 10),
          is_default: !!formData.is_default,
        });
        toast.success('Member updated');
      } else {
        await clinicsApi.addMember(clinicId, {
          user_id: parseInt(formData.user_id, 10),
          role_id: parseInt(formData.role_id, 10),
          is_default: !!formData.is_default,
        });
        toast.success('Member added');
      }
      setShowForm(false);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save member');
    }
  };

  const handleRemove = (member) => {
    setConfirmDialog({
      isOpen: true,
      message: `Remove ${member.full_name} from this clinic?`,
      onConfirm: async () => {
        try {
          await clinicsApi.removeMember(clinicId, member.user_id);
          toast.success('Member removed');
          await fetchData();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to remove member');
        }
      },
    });
  };

  const handleToggleActive = async (member) => {
    try {
      await clinicsApi.updateMember(clinicId, member.user_id, {
        is_active: !(member.is_active === 1 || member.is_active === true),
      });
      toast.success('Member updated');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/clinics"
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-1"
          >
            <HiArrowLeft className="w-4 h-4" /> Back to Clinics
          </Link>
          <h1 className="font-display text-2xl font-bold text-gray-900 truncate">
            {clinic?.name || 'Clinic'} — Members
          </h1>
          <p className="text-gray-500 mt-1">Assign users to this clinic and choose their role here.</p>
        </div>
        <button
          onClick={handleAdd}
          disabled={assignableUsers.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <HiPlus className="w-5 h-5" />
          Add Member
        </button>
      </div>

      <div className="card">
        {members.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            No members yet. Click "Add Member" to assign someone to this clinic.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-4 font-semibold text-gray-700">User</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Role</th>
                  <th className="text-center p-4 font-semibold text-gray-700">Default</th>
                  <th className="text-center p-4 font-semibold text-gray-700">Status</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.membership_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <HiOutlineUserCircle className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{member.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 capitalize">
                        {member.role_name}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      {(member.is_default === 1 || member.is_default === true) ? (
                        <HiOutlineStar className="w-5 h-5 text-yellow-500 mx-auto" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleToggleActive(member)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (member.is_active === 1 || member.is_active === true)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {(member.is_active === 1 || member.is_active === true) ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(member)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Edit role"
                        >
                          <HiPencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleRemove(member)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Remove from clinic"
                        >
                          <HiTrash className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto py-4 sm:items-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editing ? 'Edit Member' : 'Add Member'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  disabled={!!editing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                >
                  {editing ? (
                    <option value={editing.user_id}>
                      {editing.full_name} ({editing.email})
                    </option>
                  ) : (
                    <>
                      <option value="" disabled>Select a user</option>
                      {assignableUsers.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.full_name} ({u.email})
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {!editing && assignableUsers.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">All users are already members of this clinic.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="" disabled>Select a role</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id} className="capitalize">
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-900">Default clinic for this user</span>
                  <p className="text-xs text-gray-500 mt-1">The user lands in this clinic on next login.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editing ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null, message: '' })}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title="Remove Member"
        message={confirmDialog.message}
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default ClinicMembers;
